import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Field, FieldInputProps, FormikProps, getIn } from 'formik';
import { InputErrorIconRed } from 'src/assets/images';
import { SearchResults } from '../search-results/search-results';
import useDebounce from 'src/hooks/use-debounce';
import { getBusinessCategoriesFromSearch, CategoryHit } from '../../services/mcc-codes-service';
import { Hint } from 'src/features/common/types/application/pending-app';
import { ErrorCodeTooltip } from '../error-code-tooltip/error-code-tooltip';
import { Prompt } from '../prompt/prompt';
import './search-input.scss';
import { ApplicationContext } from 'src/features/onboarding/context';

interface SearchInputProps {
  placeholder: string;
  field: FieldInputProps<string>;
  form: FormikProps<string>;
  [key: string]: any;
}

export const SearchInput: React.FC<SearchInputProps> = ({ placeholder, field, form }) => {
  const [isFocussed, setIsFocussed] = useState(false);
  const debouncedSearch = useDebounce(field.value, 400);
  const [searchResults, setSearchResults] = useState<CategoryHit[]>([]);
  const { application } = useContext(ApplicationContext);

  const fullFieldName = useMemo((): string => `${field.name}.name`, [field.name]);

  const disabled = useMemo(
    (): boolean =>
      getIn(form?.initialStatus?.initialDisabled || {}, fullFieldName) ||
      form?.initialStatus?.initialDisabled?.[fullFieldName],
    [fullFieldName, form?.initialStatus?.initialDisabled],
  );

  const hints = useMemo(
    (): Hint[] =>
      getIn(form?.initialStatus?.initialHint || {}, fullFieldName) ||
      form?.initialStatus.initialHint?.[fullFieldName] ||
      [],
    [fullFieldName, form?.initialStatus.initialHint],
  );

  // @ts-ignore
  const fieldName = useMemo((): string => field.value?.name, [field]);

  const error = useMemo((): string => getIn(form.errors, field.name), [form.errors, field.name]);
  const touched = useMemo(
    (): boolean => getIn(form.touched, field.name),
    [form.touched, field.name],
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (debouncedSearch && debouncedSearch.name) {
      (async () => {
        const results: CategoryHit[] = await getBusinessCategoriesFromSearch(
          application,
          debouncedSearch.name,
          abortController,
        );

        setSearchResults(results || []);
      })();
    }
  }, [debouncedSearch]);

  return (
    <article className="search-input-container">
      <section className="input-label-container">
        <Field
          name={fullFieldName}
          disabled={disabled}
          className={`search-box ${hints.length > 0 && 'has-hints'}`}
          onFocus={() => setIsFocussed(true)}
          onChange={(event) => {
            // when backspacing, clears rest of object
            form.setFieldValue(field.name, {
              name: event?.target?.value || '',
            });
          }}
          onBlur={() => {
            form.setFieldTouched(field.name, true);
            setTimeout(() => {
              setIsFocussed(false);
            }, 100);
          }}
        />
        <hr />
        <label htmlFor={field.name} className={fieldName ? 'active' : ''}>
          {placeholder}
        </label>
        {((touched && error) || !fieldName) && (
          <section className={`under-text-area ${touched && error ? 'error' : ''}`}>
            {touched && error && <img className="error-image" src={InputErrorIconRed} />}

            {touched && error ? (
              <p>{error}</p>
            ) : (
              !fieldName && <p>Example: Restaurant, Bed & Breakfast, car hire etc</p>
            )}
          </section>
        )}

        {hints.length > 0 && !(error && touched) && (
          // @ts-ignore
          <ErrorCodeTooltip content={hints}>
            <Prompt className="under-text-area hint" />
          </ErrorCodeTooltip>
        )}
      </section>

      {isFocussed && (
        <SearchResults<CategoryHit>
          searchOptions={searchResults}
          onOptionSelected={(option) => {
            form.setFieldValue(field.name, option);
            setIsFocussed(false);
          }}
          getDisplayLabel={(option) => option.category}
          getDisplayValue={(option) => option.name}
        />
      )}
    </article>
  );
};
